import React, { useState, useEffect } from "react";
import {
  Calendar,
  Video,
  Users,
  Clock,
  Link2,
  RefreshCw,
  X,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import api from "../../services/api";

const GoogleMeetIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionDetails, setConnectionDetails] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [callbackStatus, setCallbackStatus] = useState(null);

  useEffect(() => {
    // Handle callback first
    const handleCallbackAndInit = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const googleStatus = urlParams.get("google");
      const error = urlParams.get("error");

      if (error) {
        setCallbackStatus({
          type: "error",
          message: `Connection failed: ${error}`,
        });
        // Clean URL after showing error
        setTimeout(() => {
          window.history.replaceState({}, document.title, "/google");
          setCallbackStatus(null);
        }, 5000);
      } else if (googleStatus === "connected") {
        setCallbackStatus({
          type: "success",
          message: "Google connected successfully!",
        });
        // Clean URL and refresh status
        setTimeout(() => {
          window.history.replaceState({}, document.title, "/google");
          setCallbackStatus(null);
          checkConnectionStatus();
        }, 2000);
      }

      // Always check status on mount
      await checkConnectionStatus();
    };

    handleCallbackAndInit();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      setLoading(true);
      const response = await api.getGoogleStatus();

      console.log("Google status response:", response);

      setIsConnected(response.connected);
      setConnectionDetails(response.details);

      if (response.connected) {
        // Auto-fetch upcoming meetings when connected
        await fetchUpcomingMeetings();
      } else if (response.details?.needsReauth) {
        // Show reconnection message
        setCallbackStatus({
          type: "warning",
          message: "Your Google session has expired. Please reconnect.",
        });
      }
    } catch (error) {
      console.error("Failed to check Google status:", error);
      setCallbackStatus({
        type: "error",
        message: "Failed to check connection status",
      });
    } finally {
      setLoading(false);
    }
  };

  const connectGoogle = async () => {
    try {
      setLoading(true);
      const response = await api.connectGoogle();

      if (response.authUrl) {
        // Save current timestamp to detect when user returns
        sessionStorage.setItem("googleAuthStarted", Date.now().toString());
        // Redirect to Google auth
        window.location.href = response.authUrl;
      }
    } catch (error) {
      console.error("Failed to connect Google:", error);
      setCallbackStatus({
        type: "error",
        message: `Failed to connect: ${
          error.response?.data?.message || error.message
        }`,
      });
    } finally {
      setLoading(false);
    }
  };

  const syncCalendar = async () => {
    try {
      setSyncing(true);
      const response = await api.syncGoogleCalendar();

      if (response.success) {
        await fetchUpcomingMeetings();
        await checkConnectionStatus();
        setCallbackStatus({
          type: "success",
          message: `Synced ${response.meetingsSynced || 0} meetings`,
        });
        setTimeout(() => setCallbackStatus(null), 3000);
      }
    } catch (error) {
      console.error("Failed to sync calendar:", error);
      setCallbackStatus({
        type: "error",
        message: `Sync failed: ${
          error.response?.data?.message || error.message
        }`,
      });
    } finally {
      setSyncing(false);
    }
  };

  const fetchUpcomingMeetings = async () => {
    try {
      const response = await api.getGoogleUpcomingMeetings();
      setUpcomingMeetings(
        response.meetings.map((meeting) => ({
          ...meeting,
          startTime:
            meeting.startTime ||
            meeting.startDateTime ||
            meeting.start?.dateTime,
          id: meeting.id || meeting.meetingId,
          attendeeCount: Array.isArray(meeting.attendees)
            ? meeting.attendees.length
            : 0,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
      // Don't show error for meeting fetch failures
    }
  };

  const joinMeeting = async (meetingId) => {
    try {
      const response = await api.joinGoogleMeeting(meetingId);

      if (response.success) {
        setUpcomingMeetings((prev) =>
          prev.map((m) =>
            m.id === meetingId ? { ...m, isRecording: true } : m
          )
        );
        setCallbackStatus({
          type: "success",
          message: "Recording started successfully",
        });
        setTimeout(() => setCallbackStatus(null), 3000);
      }
    } catch (error) {
      console.error("Failed to join meeting:", error);
      setCallbackStatus({
        type: "error",
        message: `Failed to start recording: ${
          error.response?.data?.message || error.message
        }`,
      });
    }
  };

  const leaveMeeting = async (meetingId) => {
    try {
      const response = await api.leaveGoogleMeeting(meetingId);

      if (response.success) {
        setUpcomingMeetings((prev) =>
          prev.map((m) =>
            m.id === meetingId ? { ...m, isRecording: false } : m
          )
        );
        setCallbackStatus({
          type: "success",
          message: "Recording stopped successfully",
        });
        setTimeout(() => setCallbackStatus(null), 3000);
      }
    } catch (error) {
      console.error("Failed to leave meeting:", error);
      setCallbackStatus({
        type: "error",
        message: `Failed to stop recording: ${
          error.response?.data?.message || error.message
        }`,
      });
    }
  };

  const disconnectGoogle = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect your Google account?"
      )
    )
      return;

    try {
      setLoading(true);
      const response = await api.disconnectGoogle();

      if (response.success) {
        setIsConnected(false);
        setConnectionDetails(null);
        setUpcomingMeetings([]);
        setCallbackStatus({
          type: "success",
          message: "Google account disconnected",
        });
        setTimeout(() => setCallbackStatus(null), 3000);
      }
    } catch (error) {
      console.error("Failed to disconnect Google:", error);
      setCallbackStatus({
        type: "error",
        message: `Failed to disconnect: ${
          error.response?.data?.message || error.message
        }`,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="bg-[#0C0E12] min-h-screen text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Status Messages */}
        {callbackStatus && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              callbackStatus.type === "success"
                ? "bg-green-600/20 border border-green-600/50"
                : callbackStatus.type === "warning"
                ? "bg-yellow-600/20 border border-yellow-600/50"
                : "bg-red-600/20 border border-red-600/50"
            }`}
          >
            {callbackStatus.type === "success" ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle
                className={`w-5 h-5 ${
                  callbackStatus.type === "warning"
                    ? "text-yellow-400"
                    : "text-red-400"
                }`}
              />
            )}
            <span>{callbackStatus.message}</span>
            <button onClick={() => setCallbackStatus(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Google Meet Integration</h1>
          <p className="text-gray-400">
            Connect your Google account to automatically record and transcribe
            meetings
          </p>
        </div>

        {/* Connection Status Card */}
        <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-600/20 rounded-lg">
                <img
                  src="/assets/meets.png"
                  alt="Google Meet"
                  className="w-8 h-8"
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-1">
                  {isConnected ? "Google Connected" : "Connect Google Meet"}
                </h3>
                {isConnected ? (
                  <div className="space-y-1">
                    <p className="text-sm text-gray-400 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Connected as: {connectionDetails?.googleEmail}
                    </p>
                    <p className="text-sm text-gray-400">
                      Last synced:{" "}
                      {connectionDetails?.lastSyncedAt
                        ? new Date(
                            connectionDetails.lastSyncedAt
                          ).toLocaleString()
                        : "Never"}
                    </p>
                    {connectionDetails?.tokenExpiresAt && (
                      <p className="text-xs text-gray-500">
                        Token expires:{" "}
                        {new Date(
                          connectionDetails.tokenExpiresAt
                        ).toLocaleString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    Grant access to your Google Calendar to enable automatic
                    meeting recording
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {isConnected ? (
                <>
                  <button
                    onClick={syncCalendar}
                    disabled={syncing || loading}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`}
                    />
                    {syncing ? "Syncing..." : "Sync Calendar"}
                  </button>
                  <button
                    onClick={disconnectGoogle}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={connectGoogle}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Link2 className="w-4 h-4" />
                  {loading ? "Connecting..." : "Connect Google"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Meetings */}
        {isConnected && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              Upcoming Google Meet Meetings
            </h2>

            {upcomingMeetings.length === 0 ? (
              <div className="bg-[#13161B] border border-gray-700 rounded-lg p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No upcoming meetings found</p>
                <button
                  onClick={syncCalendar}
                  disabled={syncing}
                  className="mt-4 text-blue-400 hover:text-blue-300 disabled:opacity-50"
                >
                  {syncing ? "Syncing..." : "Sync your calendar"}
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {upcomingMeetings.map((meeting) => (
                  <div
                    key={meeting.id}
                    className="bg-[#13161B] border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2">
                          {meeting.title || meeting.subject}
                        </h3>

                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTime(meeting.startTime)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Video className="w-4 h-4" />
                            {meeting.duration || 30} min
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {meeting.attendeeCount} attendees
                          </span>
                        </div>

                        {meeting.isRecording && (
                          <div className="mt-3 flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            <span className="text-red-400">
                              Recording in progress
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {!meeting.isRecording ? (
                          <button
                            onClick={() => joinMeeting(meeting.id)}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-2"
                          >
                            <Video className="w-4 h-4" />
                            Start Recording
                          </button>
                        ) : (
                          <button
                            onClick={() => leaveMeeting(meeting.id)}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                          >
                            Stop Recording
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Feature List */}
        {!isConnected && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">
              What you can do with Google Meet integration
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                {
                  icon: Calendar,
                  title: "Automatic Calendar Sync",
                  description:
                    "Sync your Google Calendar to see all upcoming meetings",
                },
                {
                  icon: Video,
                  title: "One-Click Recording",
                  description:
                    "Start recording any Google Meet meeting with a single click",
                },
                {
                  icon: Users,
                  title: "Participant Tracking",
                  description: "Track who attended and spoke during meetings",
                },
                {
                  icon: AlertCircle,
                  title: "Automatic Transcription",
                  description: "Get AI-powered transcripts and summaries",
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="bg-[#13161B] border border-gray-700 rounded-lg p-4"
                >
                  <feature.icon className="w-6 h-6 text-blue-400 mb-2" />
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleMeetIntegration;
